import React, { useEffect, useState } from "react";
import { saveProduct } from "../adminApi";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css"; // The Quill editor theme
import "quill-color-picker-enhance/dist/index.css"; // The new color picker CSS
// Import the custom color picker theme
import { SnowTheme } from "quill-color-picker-enhance";
import axios from "axios";
import { FaTrash, FaSpinner, FaGripVertical } from "react-icons/fa";
import { useLocation } from "react-router-dom";
import { getImageUrl } from "../../utils/imageUrl";

// Import DND Kit modules
import { DndContext, closestCenter } from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  verticalListSortingStrategy,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

const Quill = ReactQuill.Quill;
// Register the new theme with Quill
Quill.register("themes/snow-quill-color-picker-enhance", SnowTheme);

const PAGE_PLACEMENT_OPTIONS = [
  { key: "home", label: "Home" },
  { key: "wheatbag", label: "Wheatbag" },
  { key: "gift", label: "Gift" },
  { key: "kitchen", label: "Kitchen" },
  { key: "decor", label: "Decor" },
  { key: "handmade", label: "Handmade" },
];
// Create a new component for a sortable image item
const SortableImageItem = ({ image, onRemove }) => {
  // ✅ onRemove is now a single function
  // We need to use a unique ID for DND Kit, which can be the image ID or a temporary UUID for new images
  const id = image.id || image.tempId;

  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const imageSrc = image.file
    ? URL.createObjectURL(image.file)
    : getImageUrl(image.image_url);

  return (
    <div ref={setNodeRef} style={style} className="relative group w-28 h-28">
      <div {...attributes} {...listeners} className="cursor-grab">
        <FaGripVertical className="absolute top-1/2 left-1 transform -translate-y-1/2 text-gray-700 opacity-70 group-hover:opacity-100 transition-opacity z-10" />
        <img
          src={imageSrc}
          alt="Product"
          className="w-full h-full object-cover rounded-lg shadow-md border border-gray-200"
        />
      </div>
      <button
        type="button"
        onClick={() => onRemove(id)}
        className="absolute top-1 right-1 bg-red-600 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
        title="Remove image"
      >
        <FaTrash className="w-3 h-3" />
      </button>
    </div>
  );
};

export default function ProductForm({ product, onSuccess }) {
  const location = useLocation();

  const normalizeTags = (value) => {
    if (!value) return [];

    if (Array.isArray(value)) {
      return [
        ...new Set(
          value
            .map((tag) =>
              String(tag || "")
                .trim()
                .toLowerCase(),
            )
            .filter(Boolean),
        ),
      ];
    }

    return [
      ...new Set(
        String(value)
          .split(",")
          .map((tag) => tag.trim().toLowerCase())
          .filter(Boolean),
      ),
    ];
  };

  const [formData, setFormData] = useState({
    name_en: "",
    name_ar: "",
    name_tr: "",
    name_ja: "",
    description_en: "",
    description_ar: "",
    description_tr: "",
    description_ja: "",
    category_id: "",
    price: "",
    sale_price: "",
    stock_quantity: "",
    model_code: "",
    variation_type: "",
    variation_value: "",
    sku: "",
    tags: [],
    page_placements: [],
    trendyol_link: "",
    amazon_link: "",
    amazon_japan_link: "",
    hepsiburada_link: "",
    product_status: "active",
    product_slug: "",
    meta_description: "",
    meta_keywords: "",
    related_product_ids: [],
    images: [], // ✅ This state will now hold ALL images (new and existing)
  });
  const [tagsInput, setTagsInput] = useState("");
  const [relatedIdsInput, setRelatedIdsInput] = useState("");

  const [categories, setCategories] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const BACKEND_URL =
          process.env.REACT_APP_BACKEND_URL || "http://localhost:5000";
        const res = await axios.get(`${BACKEND_URL}/api/categories`);
        setCategories(res.data || []);
      } catch (err) {
        console.error("Failed to fetch categories:", err);
      }
    };
    fetchCategories();

    const isVariant = location.state?.isVariant;
    const copiedProduct = location.state?.product;
    const copiedPagePlacements = Array.isArray(copiedProduct?.pagePlacements)
      ? copiedProduct.pagePlacements.map((placement) => placement.page_key)
      : Array.isArray(copiedProduct?.page_placements)
        ? copiedProduct.page_placements
        : [];

    if (isVariant && copiedProduct) {
      setFormData({
        name_en: "Copied " + copiedProduct.name_en,
        name_ar: copiedProduct.name_ar,
        name_tr: copiedProduct.name_tr,
        name_ja: copiedProduct.name_ja,
        description_en: copiedProduct.description_en,
        description_ar: copiedProduct.description_ar,
        description_tr: copiedProduct.description_tr,
        description_ja: copiedProduct.description_ja,
        category_id: copiedProduct.category_id,
        price: copiedProduct.price,
        sale_price: copiedProduct.sale_price || "",
        stock_quantity: copiedProduct.stock_quantity,
        model_code: copiedProduct.model_code,
        variation_type: copiedProduct.variation_type,
        variation_value: copiedProduct.variation_value,
        sku: "",
        tags: normalizeTags(copiedProduct.tags),
        page_placements: copiedPagePlacements,
        trendyol_link: "",
        amazon_link: "",
        amazon_japan_link: "",
        hepsiburada_link: "",
        product_status: copiedProduct.product_status || "active",
        product_slug: "",
        meta_description: copiedProduct.meta_description || "",
        meta_keywords: copiedProduct.meta_keywords || "",
        related_product_ids: [],
        images: copiedProduct.images, // ✅ Use images from the parent product
      });
      setTagsInput(normalizeTags(copiedProduct.tags).join(", "));
      setRelatedIdsInput("");
    } else if (product) {
      const sortedImages = [...product.images].sort(
        (a, b) => a.sort_order - b.sort_order,
      );
      const normalizedTags = normalizeTags(product.tags);
      setFormData({
        name_en: product.name_en || "",
        name_ar: product.name_ar || "",
        name_tr: product.name_tr || "",
        name_ja: product.name_ja || "",
        description_en: product.description_en || "",
        description_ar: product.description_ar || "",
        description_tr: product.description_tr || "",
        description_ja: product.description_ja || "",
        category_id: product.category_id || "",
        price: product.price || "",
        sale_price: product.sale_price || "",
        stock_quantity: product.stock_quantity ?? "",
        model_code: product.model_code || "",
        variation_type: product.variation_type || "",
        variation_value: product.variation_value || "",
        sku: product.sku || "",
        tags: normalizedTags,
        page_placements: Array.isArray(product.pagePlacements)
          ? product.pagePlacements.map((placement) => placement.page_key)
          : Array.isArray(product.page_placements)
            ? product.page_placements
            : [],
        trendyol_link: product.trendyol_link || "",
        amazon_link: product.amazon_link || "",
        amazon_japan_link: product.amazon_japan_link || "",
        hepsiburada_link: product.hepsiburada_link || "",
        product_status: product.product_status || "active",
        product_slug: product.product_slug || "",
        meta_description: product.meta_description || "",
        meta_keywords: product.meta_keywords || "",
        related_product_ids: product.related_product_ids || [],
        images: sortedImages, // ✅ Populate the single images state
      });
      setTagsInput(normalizedTags.join(", "));
      setRelatedIdsInput(
        Array.isArray(product.related_product_ids)
          ? product.related_product_ids.join(", ")
          : "",
      );
    }
  }, [product, location.state]);

  const handleChange = (e) => {
    const { name, value, files } = e.target;
    if (files) {
      // ✅ Handle new file uploads
      const newImages = Array.from(files).map((file) => ({
        tempId: Date.now() + Math.random(), // Give a temporary unique ID for DND Kit
        file: file,
      }));
      setFormData({
        ...formData,
        images: [...formData.images, ...newImages],
      });
    } else {
      setFormData({ ...formData, [name]: value });
    }
  };

  const handleDescriptionChange = (html, lang) => {
    setFormData((prevData) => ({
      ...prevData,
      [`description_${lang}`]: html,
    }));
  };

  const handlePlacementToggle = (pageKey) => {
    setFormData((prevData) => {
      const alreadySelected = prevData.page_placements.includes(pageKey);
      return {
        ...prevData,
        page_placements: alreadySelected
          ? prevData.page_placements.filter((key) => key !== pageKey)
          : [...prevData.page_placements, pageKey],
      };
    });
  };

  const handleTagsChange = (value) => {
    setTagsInput(value);
    setFormData((prevData) => ({
      ...prevData,
      tags: normalizeTags(value),
    }));
  };

  const handleRelatedIdsChange = (value) => {
    setRelatedIdsInput(value);
    const parsedIds = [
      ...new Set(
        String(value || "")
          .split(",")
          .map((item) => Number(item.trim()))
          .filter((id) => Number.isInteger(id) && id > 0),
      ),
    ];

    setFormData((prevData) => ({
      ...prevData,
      related_product_ids: parsedIds,
    }));
  };

  // ✅ Single function to remove any image by its DND Kit ID
  const handleRemoveImage = (imageId) => {
    setFormData({
      ...formData,
      images: formData.images.filter(
        (img) => (img.id || img.tempId) !== imageId,
      ),
    });
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (active.id !== over.id) {
      const oldIndex = formData.images.findIndex(
        (img) => (img.id || img.tempId) === active.id,
      );
      const newIndex = formData.images.findIndex(
        (img) => (img.id || img.tempId) === over.id,
      );
      const newOrder = arrayMove(formData.images, oldIndex, newIndex);
      setFormData({ ...formData, images: newOrder });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);
    try {
      // Build multipart payload explicitly so backend receives predictable field types.
      const newFiles = formData.images
        .filter((img) => img.file)
        .map((img) => img.file);
      // Re-index sort_order based on current drag-and-drop array position
      const existingImages = formData.images
        .filter((img) => !img.file)
        .map((img, index) => ({
          id: Number(img.id),
          image_url: img.image_url,
          sort_order: index,
        }))
        .filter((img) => Number.isInteger(img.id));

      const multipartData = new FormData();
      multipartData.append("name_en", formData.name_en || "");
      multipartData.append("name_ar", formData.name_ar || "");
      multipartData.append("name_tr", formData.name_tr || "");
      multipartData.append("name_ja", formData.name_ja || "");
      multipartData.append("description_en", formData.description_en || "");
      multipartData.append("description_ar", formData.description_ar || "");
      multipartData.append("description_tr", formData.description_tr || "");
      multipartData.append("description_ja", formData.description_ja || "");
      multipartData.append("category_id", String(formData.category_id || ""));
      multipartData.append("price", String(formData.price || ""));
      multipartData.append("sale_price", String(formData.sale_price || ""));
      multipartData.append(
        "stock_quantity",
        String(formData.stock_quantity ?? ""),
      );
      multipartData.append("model_code", formData.model_code || "");
      multipartData.append("variation_type", formData.variation_type || "");
      multipartData.append("variation_value", formData.variation_value || "");
      multipartData.append("sku", formData.sku || "");
      multipartData.append("trendyol_link", formData.trendyol_link || "");
      multipartData.append("amazon_link", formData.amazon_link || "");
      multipartData.append(
        "amazon_japan_link",
        formData.amazon_japan_link || "",
      );
      multipartData.append("hepsiburada_link", formData.hepsiburada_link || "");
      multipartData.append(
        "product_status",
        formData.product_status || "active",
      );
      multipartData.append("product_slug", formData.product_slug || "");
      multipartData.append("meta_description", formData.meta_description || "");
      multipartData.append("meta_keywords", formData.meta_keywords || "");
      multipartData.append(
        "related_product_ids",
        JSON.stringify(formData.related_product_ids || []),
      );

      multipartData.append("tags", JSON.stringify(formData.tags || []));
      multipartData.append(
        "page_placements",
        JSON.stringify(formData.page_placements || []),
      );
      multipartData.append("existingImages", JSON.stringify(existingImages));

      newFiles.forEach((file) => {
        multipartData.append("images", file);
      });

      await saveProduct(multipartData, product?.product_id);
      if (onSuccess) onSuccess();
    } catch (err) {
      console.error("Error saving product:", err.response?.data || err);
      setError(
        err.response?.data?.error ||
          err.response?.data?.message ||
          "Failed to save product. Please try again.",
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    window.history.back();
  };

  const isCreatingNew = !product;

  const modules = {
    toolbar: [
      [{ header: [1, 2, false] }],
      ["bold", "italic", "underline", "strike", "blockquote"],
      [
        { list: "ordered" },
        { list: "bullet" },
        { indent: "-1" },
        { indent: "+1" },
      ],
      ["link", "image"],
      [{ color: [] }, { background: [] }], // Add these two lines for the color wheel
      ["clean"],
    ],
  };

  const formats = [
    "header",
    "bold",
    "italic",
    "underline",
    "strike",
    "blockquote",
    "list",
    "bullet",
    "indent",
    "link",
    "image",
    "color",
    "background", // Add these two for colors
  ];

  return (
    <form
      onSubmit={handleSubmit}
      className="max-w-4xl mx-auto p-8 bg-white shadow-xl rounded-2xl space-y-6 border border-gray-200"
    >
      <h2 className="text-3xl font-extrabold text-gray-900 text-center mb-10">
        {isCreatingNew ? "Add New Product" : "Edit Product"}
      </h2>

      {/* ... (rest of the form fields remain the same) */}

      {error && (
        <div
          className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative mb-4"
          role="alert"
        >
          <span className="block sm:inline">{error}</span>
        </div>
      )}

      {/* --- Basic Information Section --- */}
      <div>
        <h3 className="text-xl font-bold text-gray-700 border-b pb-2 mb-4">
          Basic Information
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-2">
            <label className="block text-sm font-semibold text-gray-700">
              Name (EN)
            </label>
            <input
              type="text"
              name="name_en"
              value={formData.name_en}
              onChange={handleChange}
              className="w-full border-gray-300 rounded-lg shadow-sm focus:border-blue-500 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
            />
          </div>
          <div className="space-y-2">
            <label className="block text-sm font-semibold text-gray-700">
              Name (AR)
            </label>
            <input
              type="text"
              name="name_ar"
              value={formData.name_ar}
              onChange={handleChange}
              className="w-full border-gray-300 rounded-lg shadow-sm focus:border-blue-500 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
            />
          </div>
          <div className="space-y-2">
            <label className="block text-sm font-semibold text-gray-700">
              Name (TR)
            </label>
            <input
              type="text"
              name="name_tr"
              value={formData.name_tr}
              onChange={handleChange}
              className="w-full border-gray-300 rounded-lg shadow-sm focus:border-blue-500 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
            />
          </div>
          <div className="space-y-2">
            <label className="block text-sm font-semibold text-gray-700">
              Name (JA)
            </label>
            <input
              type="text"
              name="name_ja"
              value={formData.name_ja}
              onChange={handleChange}
              className="w-full border-gray-300 rounded-lg shadow-sm focus:border-blue-500 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
            />
          </div>
          <div className="space-y-2">
            <label className="block text-sm font-semibold text-gray-700">
              Category
            </label>
            <select
              name="category_id"
              value={formData.category_id}
              onChange={handleChange}
              className="w-full border-gray-300 rounded-lg shadow-sm focus:border-blue-500 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
            >
              <option value="">Select a category</option>
              {categories.map((cat) => (
                <option key={cat.id} value={cat.id}>
                  {cat.name}
                </option>
              ))}
            </select>
          </div>
          <div className="md:col-span-2 space-y-2">
            <label className="block text-sm font-semibold text-gray-700">
              Description (EN)
            </label>
            <ReactQuill
              theme="snow-quill-color-picker-enhance"
              value={formData.description_en}
              onChange={(html) => handleDescriptionChange(html, "en")}
              modules={modules}
              formats={formats}
            />
            <label className="block text-sm font-semibold text-gray-700">
              Description (AR)
            </label>
            <ReactQuill
              theme="snow-quill-color-picker-enhance"
              value={formData.description_ar}
              onChange={(html) => handleDescriptionChange(html, "ar")}
              modules={modules}
              formats={formats}
            />
            <label className="block text-sm font-semibold text-gray-700">
              Description (TR)
            </label>
            <ReactQuill
              theme="snow-quill-color-picker-enhance"
              value={formData.description_tr}
              onChange={(html) => handleDescriptionChange(html, "tr")}
              modules={modules}
              formats={formats}
            />
            <label className="block text-sm font-semibold text-gray-700">
              Description (JA)
            </label>
            <ReactQuill
              theme="snow-quill-color-picker-enhance"
              value={formData.description_ja}
              onChange={(html) => handleDescriptionChange(html, "ja")}
              modules={modules}
              formats={formats}
            />
          </div>
        </div>
      </div>

      {/* --- Pricing & Stock Section --- */}
      <div>
        <h3 className="text-xl font-bold text-gray-700 border-b pb-2 mb-4">
          Pricing & Stock
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-2">
            <label className="block text-sm font-semibold text-gray-700">
              Price
            </label>
            <input
              type="number"
              step="0.01"
              name="price"
              value={formData.price}
              onChange={handleChange}
              className="w-full border-gray-300 rounded-lg shadow-sm focus:border-blue-500 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
            />
          </div>
          <div className="space-y-2">
            <label className="block text-sm font-semibold text-gray-700">
              Sale Price (Optional)
            </label>
            <input
              type="number"
              step="0.01"
              name="sale_price"
              value={formData.sale_price}
              onChange={handleChange}
              placeholder="Leave empty for no sale"
              className="w-full border-gray-300 rounded-lg shadow-sm focus:border-blue-500 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
            />
            <p className="text-xs text-gray-500">
              Shows as discounted price on storefront
            </p>
          </div>
          <div className="space-y-2">
            <label className="block text-sm font-semibold text-gray-700">
              Stock Quantity
            </label>
            <input
              type="number"
              name="stock_quantity"
              value={formData.stock_quantity}
              onChange={handleChange}
              className="w-full border-gray-300 rounded-lg shadow-sm focus:border-blue-500 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
            />
          </div>
          <div className="space-y-2">
            <label className="block text-sm font-semibold text-gray-700">
              Product Status
            </label>
            <select
              name="product_status"
              value={formData.product_status}
              onChange={handleChange}
              className="w-full border-gray-300 rounded-lg shadow-sm focus:border-blue-500 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
            >
              <option value="draft">Draft (Hidden)</option>
              <option value="active">Active (Visible)</option>
              <option value="hidden">Hidden</option>
              <option value="discontinued">Discontinued</option>
            </select>
            <p className="text-xs text-gray-500">
              Controls product visibility and status
            </p>
          </div>
        </div>
      </div>

      {/* --- SEO & Pro Fields Section --- */}
      <div>
        <h3 className="text-xl font-bold text-gray-700 border-b pb-2 mb-4">
          SEO & Advanced
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-2">
            <label className="block text-sm font-semibold text-gray-700">
              URL Slug (Optional)
            </label>
            <input
              type="text"
              name="product_slug"
              value={formData.product_slug}
              onChange={handleChange}
              placeholder="my-product-name"
              className="w-full border-gray-300 rounded-lg shadow-sm focus:border-blue-500 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
            />
            <p className="text-xs text-gray-500">
              SEO-friendly URL: /products/your-slug
            </p>
          </div>
          <div className="md:col-span-2 space-y-2">
            <label className="block text-sm font-semibold text-gray-700">
              Meta Description (Optional)
            </label>
            <textarea
              name="meta_description"
              value={formData.meta_description}
              onChange={handleChange}
              placeholder="Brief description for search engines (160 chars recommended)"
              rows="2"
              maxLength="160"
              className="w-full border-gray-300 rounded-lg shadow-sm focus:border-blue-500 focus:ring focus:ring-blue-200 focus:ring-opacity-50 resize-none"
            />
            <p className="text-xs text-gray-500">
              {formData.meta_description.length}/160 characters
            </p>
          </div>
          <div className="md:col-span-2 space-y-2">
            <label className="block text-sm font-semibold text-gray-700">
              Meta Keywords (Optional)
            </label>
            <input
              type="text"
              name="meta_keywords"
              value={formData.meta_keywords}
              onChange={handleChange}
              placeholder="keyword1, keyword2, keyword3"
              className="w-full border-gray-300 rounded-lg shadow-sm focus:border-blue-500 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
            />
            <p className="text-xs text-gray-500">
              Comma-separated keywords for SEO
            </p>
          </div>
          <div className="md:col-span-2 space-y-2">
            <label className="block text-sm font-semibold text-gray-700">
              Bought Together Product IDs (Optional)
            </label>
            <input
              type="text"
              value={relatedIdsInput}
              onChange={(e) => handleRelatedIdsChange(e.target.value)}
              placeholder="12, 45, 78"
              className="w-full border-gray-300 rounded-lg shadow-sm focus:border-blue-500 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
            />
            <p className="text-xs text-gray-500">
              Enter comma-separated product IDs to show in the Related Products
              section.
            </p>
          </div>
        </div>
      </div>

      {/* --- Additional Details Section --- */}
      <div>
        <h3 className="text-xl font-bold text-gray-700 border-b pb-2 mb-4">
          Additional Details
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-2">
            <label className="block text-sm font-semibold text-gray-700">
              Model Code
            </label>
            <input
              type="text"
              name="model_code"
              value={formData.model_code}
              onChange={handleChange}
              className="w-full border-gray-300 rounded-lg shadow-sm focus:border-blue-500 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
            />
          </div>
          <div className="space-y-2">
            <label className="block text-sm font-semibold text-gray-700">
              SKU
            </label>
            <input
              type="text"
              name="sku"
              value={formData.sku}
              onChange={handleChange}
              className="w-full border-gray-300 rounded-lg shadow-sm focus:border-blue-500 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
            />
          </div>
          <div className="space-y-2">
            <label className="block text-sm font-semibold text-gray-700">
              Variation Type
            </label>
            <select
              name="variation_type"
              value={formData.variation_type}
              onChange={handleChange}
              className="w-full border-gray-300 rounded-lg shadow-sm focus:border-blue-500 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
            >
              <option value="">None</option>
              <option value="Color">Color</option>
              <option value="Size">Size</option>
            </select>
          </div>
          <div className="space-y-2">
            <label className="block text-sm font-semibold text-gray-700">
              Variation Value
            </label>
            <input
              type="text"
              name="variation_value"
              value={formData.variation_value}
              onChange={handleChange}
              className="w-full border-gray-300 rounded-lg shadow-sm focus:border-blue-500 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
            />
          </div>
          <div className="space-y-2">
            <label className="block text-sm font-semibold text-gray-700">
              Trendyol Link
            </label>
            <input
              type="text"
              name="trendyol_link"
              value={formData.trendyol_link}
              onChange={handleChange}
              className="w-full border-gray-300 rounded-lg shadow-sm focus:border-blue-500 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
            />
          </div>
          <div className="space-y-2">
            <label className="block text-sm font-semibold text-gray-700">
              Amazon Link
            </label>
            <input
              type="text"
              name="amazon_link"
              value={formData.amazon_link}
              onChange={handleChange}
              className="w-full border-gray-300 rounded-lg shadow-sm focus:border-blue-500 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
            />
          </div>
          <div className="space-y-2">
            <label className="block text-sm font-semibold text-gray-700">
              Amazon Japan Link
            </label>
            <input
              type="text"
              name="amazon_japan_link"
              value={formData.amazon_japan_link}
              onChange={handleChange}
              className="w-full border-gray-300 rounded-lg shadow-sm focus:border-blue-500 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
            />
          </div>
          <div className="space-y-2">
            <label className="block text-sm font-semibold text-gray-700">
              Hepsiburada Link
            </label>
            <input
              type="text"
              name="hepsiburada_link"
              value={formData.hepsiburada_link}
              onChange={handleChange}
              className="w-full border-gray-300 rounded-lg shadow-sm focus:border-blue-500 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
            />
          </div>

          <div className="md:col-span-2 space-y-2">
            <label className="block text-sm font-semibold text-gray-700">
              Tags
            </label>
            <input
              type="text"
              value={tagsInput}
              onChange={(e) => handleTagsChange(e.target.value)}
              placeholder="gift, relaxation, lavender, handmade"
              className="w-full border-gray-300 rounded-lg shadow-sm focus:border-blue-500 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
            />
            <p className="text-xs text-gray-500">
              Comma-separated tags for filtering, collections, and
              merchandising.
            </p>
            {formData.tags.length > 0 && (
              <div className="flex flex-wrap gap-2 pt-1">
                {formData.tags.map((tag) => (
                  <span
                    key={tag}
                    className="inline-flex items-center rounded-full bg-gray-100 px-2.5 py-1 text-xs font-medium text-gray-700"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            )}
          </div>

          <div className="md:col-span-2 space-y-3">
            <label className="block text-sm font-semibold text-gray-700">
              Show Product In Pages
            </label>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
              {PAGE_PLACEMENT_OPTIONS.map((option) => {
                const checked = formData.page_placements.includes(option.key);
                return (
                  <label
                    key={option.key}
                    className={`flex items-center gap-2 rounded-lg border px-3 py-2 cursor-pointer transition-colors ${
                      checked
                        ? "border-blue-500 bg-blue-50"
                        : "border-gray-300 bg-white hover:bg-gray-50"
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => handlePlacementToggle(option.key)}
                      className="rounded border-gray-300"
                    />
                    <span className="text-sm text-gray-700">
                      {option.label}
                    </span>
                  </label>
                );
              })}
            </div>
            <p className="text-xs text-gray-500">
              Select where this product appears in home scrolling sections.
            </p>
          </div>
        </div>
      </div>

      {/* --- Image Section --- */}
      <div>
        <h3 className="text-xl font-bold text-gray-700 border-b pb-2 mb-4">
          Product Images
        </h3>
        <div className="space-y-2">
          <label className="block text-sm font-semibold text-gray-700">
            Upload Images
          </label>
          <div className="flex items-center space-x-2">
            <input
              type="file"
              name="images"
              multiple
              accept="image/jpeg,image/png,image/webp,image/gif"
              onChange={handleChange}
              className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
            />
          </div>
          <p className="text-xs text-gray-500">
            You can upload up to 30 images per save, with up to 20MB per image.
          </p>
        </div>

        {/* ✅ Unified DND section for ALL images */}
        {formData.images.length > 0 && (
          <div className="mt-6">
            <h4 className="text-lg font-semibold text-gray-600 mb-2">
              Current Images (Drag to reorder)
            </h4>
            {/* ✅ Add max-height and overflow-y-auto to the container */}
            <div className="max-h-80 overflow-y-auto pr-2">
              <DndContext
                collisionDetection={closestCenter}
                onDragEnd={handleDragEnd}
              >
                <SortableContext
                  items={formData.images.map((img) => img.id || img.tempId)}
                  strategy={verticalListSortingStrategy}
                >
                  <div className="flex flex-wrap gap-4">
                    {formData.images.map((img) => (
                      <SortableImageItem
                        key={img.id || img.tempId}
                        image={img}
                        onRemove={handleRemoveImage}
                      />
                    ))}
                  </div>
                </SortableContext>
              </DndContext>
            </div>
          </div>
        )}
      </div>

      <div className="flex justify-end space-x-4 mt-8">
        <button
          type="button"
          onClick={handleCancel}
          className="flex items-center justify-center space-x-2 px-6 py-3 border border-gray-300 text-lg font-bold rounded-md shadow-sm text-gray-700 bg-white hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 transition-colors duration-200"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={isSubmitting}
          className="w-full md:w-auto flex items-center justify-center space-x-2 px-6 py-3 border border-transparent text-lg font-bold rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isSubmitting ? (
            <FaSpinner className="animate-spin" />
          ) : isCreatingNew ? (
            "Add Product"
          ) : (
            "Update Product"
          )}
        </button>
      </div>
    </form>
  );
}
